<template>
  <div class="invoices-container navbar-gap">
    <b-row class="row">
      <b-col :sm="12">
        <h1 class="page-title section-title title-slash">
          {{ $t('importers.invoices.title') }}
        </h1>
      </b-col>
    </b-row>
    <alert v-if="esignCount > 0">
      <template slot="icon">
        <img
          src="@/assets/images/common/error-outline-white.svg"
          alt="Error Icon"
        />
      </template>
      <template slot="message">
        {{
          $tc('alerts.dashboard.esignPending', esignCount, {
            count: esignCount
          })
        }}
      </template>
      <template slot="cta">
        <b-button variant="white" size="lg" :to="{ name: 'listEsign' }">
          {{ $tc('alerts.dashboard.esignPendingCta', esignCount) }}
        </b-button>
      </template>
    </alert>
    <b-row>
      <b-col :md="12" :lg="9" :xl="9">
        <b-form-radio-group
          v-if="!searchTerm"
          class="invoices-filters d-sm-flex flex-wrap"
          id="btn-radios"
          buttons
          button-variant="outline-primary"
          v-model="selectedFilter"
          name="radioBtnOutline"
        >
          <b-form-radio value="pending">
            {{ $t('importers.invoices.pending') }}
            {{ displayData ? `(${displayData.pending})` : '' }}
          </b-form-radio>
          <b-form-radio value="outstanding">
            {{ $t('importers.invoices.outstanding') }}
            {{ displayData ? `(${displayData.outstanding})` : '' }}
          </b-form-radio>
          <b-form-radio value="received">
            {{ $t('importers.invoices.received') }}
            {{ displayData ? `(${displayData.received})` : '' }}
          </b-form-radio>
          <b-form-radio value="settled">
            {{ $t('importers.invoices.settled') }}
            {{ displayData ? `(${displayData.settled})` : '' }}
          </b-form-radio>
        </b-form-radio-group>
        <p class="lead search-title" v-else>
          {{ $t('importers.invoices.searchResults') }} '{{ searchTerm }}'
          <a href="#" @click.prevent="removeFilter" class="remove-filter">
            <b-icon-x />
          </a>
        </p>
      </b-col>
      <b-col :md="12" :lg="3" :xl="3" class="mb-3">
        <b-form>
          <b-input
            class="invoice-search-input"
            v-model="searchTerm"
            type="search"
            :placeholder="$t('importers.invoices.searchInvoice')"
          />
        </b-form>
      </b-col>
    </b-row>
    <b-row>
      <b-col :sm="12">
        <template>
          <template v-if="filteredInvoices.length > 0">
            <b-table
              responsive
              v-if="filteredInvoices.length > 0"
              :items="filteredInvoices"
              :fields="invoiceTableFields"
              class="drip-v2-table table-borderless"
              tbody-tr-class="drip-tr-class"
              thead-class="drip-table-head"
              :per-page="20"
              :current-page="currentPage"
            >
              <template v-slot:cell(exporter)="data">
                <div style="min-width: 128px">
                  {{ data.value }}
                </div>
              </template>
              <template v-slot:cell(value)="data">
                <div v-if="data.value">
                  {{ data.value | currency(data.item.currsym) }}
                </div>
                <div v-else>-</div>
              </template>
              <template v-slot:cell(po_no)="data">
                <div v-if="data.value" style="min-width: 128px">
                  {{ data.value }}
                </div>
                <div v-else>-</div>
              </template>
              <template v-slot:cell(advice_collection_pending)="data">
                <div v-if="data.value">
                  {{ data.value | currency(data.item.currsym) }}
                </div>
                <div v-else>-</div>
              </template>
              <template v-slot:cell(repayment)="data">
                <div v-if="data.value">
                  {{ data.value | moment('MMMM D, YYYY') }}
                </div>
                <div v-else>-</div>
              </template>
              <template v-slot:cell(invoice_date)="data">
                <div v-if="data.value">
                  {{ data.value | moment('MMMM D, YYYY') }}
                </div>
                <div v-else>-</div>
              </template>
              <template v-slot:cell(documents_public)="data">
                <b-dropdown
                  v-if="data.value && data.value.length"
                  right
                  text="Documents"
                  variant="link"
                  class="dropdown-link"
                >
                  <b-dropdown-item
                    v-for="document in data.value"
                    :key="document.id"
                    @click="showDocument(document)"
                  >
                    {{ document.tags_value }}
                  </b-dropdown-item>
                </b-dropdown>
                <div v-else>No documents uploaded</div>
              </template>
            </b-table>
            <b-pagination
              :total-rows="filteredInvoices.length"
              v-model="currentPage"
              :per-page="20"
            />
          </template>
          <template v-else>
            <b-row>
              <b-col :md="10" :lg="8" :xl="5" class="mx-auto">
                <div
                  v-for="(val, index) in [
                    'pending',
                    'received',
                    'outstanding',
                    'settled'
                  ]"
                  :key="index"
                >
                  <template v-if="selectedFilter === val">
                    <p v-if="loadingTable[val]" class="lead text-center">
                      <loader :height="500" />
                    </p>
                    <p v-else class="lead text-center">
                      {{ $t('importers.invoices.noInvoices') }}
                    </p>
                  </template>
                </div>
              </b-col>
            </b-row>
          </template>
        </template>
      </b-col>
    </b-row>
    <b-modal
      ref="documentModal"
      size="lg"
      hide-footer
      @hide="documentUrl = null"
      class="document-modal"
    >
      <iframe ref="documentIframe" class="document-iframe" :src="documentUrl" />
    </b-modal>
  </div>
</template>
<script>
import axios from 'axios'
import moment from 'moment'
import Loader from '@/components/common/Loader'
import Alert from '@/components/common/Alert'
import importersService from '@/services/importers'
import invoiceService from '@/services/invoice'

export default {
  data() {
    return {
      esignCount: 0,
      searchTerm: '',
      documentUrl: null,
      currentPage: 1,
      pending: '',
      outstanding: '',
      received: '',
      settled: '',
      invoiceStageCounters: null,
      selectedFilter: this.$route.params.selectedFilter || 'pending',
      loadingTable: {
        pending: true,
        outstanding: true,
        received: true,
        settled: true
      }
    }
  },
  computed: {
    filteredInvoices() {
      if (this.searchTerm) {
        return this.invoices.filter((invoice) => {
          return invoice.reference.indexOf(this.searchTerm) > -1
        })
      } else {
        return this[this.selectedFilter]
      }
    },
    invoices() {
      return [
        ...this.pending,
        ...this.outstanding,
        ...this.received,
        ...this.settled
      ]
    },
    displayData() {
      if (this.invoiceStageCounters) {
        const displayData = {}
        for (const group in this.invoiceStageCounters.groups) {
          displayData[group] = 0
          this.invoiceStageCounters.groups[group].forEach((element) => {
            if (
              typeof this.invoiceStageCounters.counters[element] !== 'undefined'
            ) {
              displayData[group] += this.invoiceStageCounters.counters[element]
            }
          })
        }
        return displayData
      } else {
        return false
      }
    },
    invoiceTableFields() {
      return this.$t('importers.invoices.invoiceTableFields')
    }
  },
  async mounted() {
    document.title = 'Invoices | Drip Portal'
    this.getInvoiceMeta()
    await this.getAllInvoices('pending_advance_invoices', 'pending')
    this.loadingTable.pending = false
    this.getSignableInvoices()
    await this.getAllInvoices('advanced_invoices', 'outstanding')
    this.loadingTable.outstanding = false
    await this.getAllInvoices('pending_settlement_invoices', 'received')
    this.loadingTable.received = false
    await this.getAllInvoices('settled_invoices', 'settled')
    this.loadingTable.settled = false
  },
  methods: {
    getSignableInvoices() {
      return axios.all([this.getAckInvoices(), this.getBoeInvoices()]).then(
        axios.spread((ackInvoices, boeInvoices) => {
          this.esignCount +=
            ackInvoices.data.invoice_ack_signable_invoices.length
          this.esignCount +=
            boeInvoices.data.invoice_boe_signable_invoices.length
        })
      )
    },
    getAckInvoices() {
      return importersService.getAckInvoices(
        this.$store.state.account.accountId,
        {
          include: ['invoice_ack_signable_invoices']
        }
      )
    },
    getBoeInvoices() {
      return importersService.getAckInvoices(
        this.$store.state.account.accountId,
        {
          include: ['invoice_boe_signable_invoices']
        }
      )
    },
    removeFilter() {
      this.searchTerm = ''
    },
    async getAllInvoices(endpoint, dataProp) {
      return await invoiceService
        .getAllInvoicesByEndpoint(
          this.$store.state.account.accountId,
          endpoint,
          {
            serializer: 'Business::InvoiceListSerializer'
          }
        )
        .then((res) => {
          this[dataProp] = this.sortData(res.data)
        })
        .catch((err) => {
          console.log(err)
        })
    },
    sortData(arr) {
      return arr.sort((a, b) => {
        return moment(b.created_at) - moment(a.created_at)
      })
    },
    getInvoiceMeta() {
      return importersService
        .fetchInvoiceStageCounters(this.$store.state.account.accountId)
        .then((res) => {
          this.invoiceStageCounters = res.data
        })
    },
    showDocument(document) {
      this.$refs.documentModal.show()
      this.documentUrl = `${process.env.VUE_APP_DRIP_WEB_HOST}/documents/${document.id}/auth/${document.auth_token}`
    }
  },
  components: {
    Loader,
    Alert
  }
}
</script>
<style lang="scss">
@import '../../assets/scss/variables';
.btn-white {
  // todo: varibles
  background: $white;
}
.navbar-gap {
  margin-top: 7rem;
}
</style>
